import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { LookupsParams, Organization } from "@/modules/common/dtos/index.v2";
import { queryCommonFlags } from "@/api/constants";
import { fetchLeadTypesLookups } from "@/modules/common/api/index.v2";
import { debounce, isEqual } from "lodash";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import { FilterOptionsParams } from "@/modules/common/types";

const useLeadTypesFilterOptions = (
  { initialParams }: FilterOptionsParams = {},
  extraParams?: {
    includePoolTypes?: boolean;
    excludePoolTypes?: boolean;
    allowAllFunnels?: boolean;
    includeOnlyDefaultPerCategory?: boolean;
    includeOnlyAllowReferrals?: boolean;
  }
) => {
  const { selectedOrganization, selectedOrganizations } = useOrganization();
  const [searchTerm, setSearchTerm] = useState("");

  const [queryParams, setQueryParams] = useState<LookupsParams>(() => ({
    page: 1,
    pageSize: 50,
    ...initialParams,
  }));
  const storedOrgName = localStorage.getItem("selectedOrganizationName");
  const homeLocalStorageFilters = localStorage.getItem(
    `leadSearchFilters${storedOrgName}`
  );

  const parsedHomeFilters = homeLocalStorageFilters
    ? JSON.parse(homeLocalStorageFilters)
    : "";

  const nextActionsLocalStorageFilters = localStorage.getItem(
    `nextActionsSearchFilters${storedOrgName}`
  );

  const parsedNextActionsFilters = nextActionsLocalStorageFilters
    ? JSON.parse(nextActionsLocalStorageFilters)
    : "";

  const updateParams = useCallback((newParams: Partial<LookupsParams>) => {
    setQueryParams((prev) => {
      const updated = { ...prev, ...newParams };
      return isEqual(prev, updated) ? prev : updated;
    });
  }, []);

  const handleSearchChange = useMemo(
    () =>
      debounce((event) => {
        const newSearchTerm = event?.target?.value || "";
        setSearchTerm(newSearchTerm);
        updateParams({ name: newSearchTerm });
      }, 300),
    []
  );

  useEffect(() => {
    const newParams: Partial<LookupsParams> = {};

    if (initialParams?.organizationUIds) {
      newParams.organizationUIds = initialParams.organizationUIds;
    } else if (selectedOrganizations && selectedOrganizations.length > 0) {
      newParams.organizationUIds = selectedOrganizations
        .map((org: Organization) => org.uid)
        .join(",");
    } else if (selectedOrganization?.uid) {
      newParams.organizationUIds = selectedOrganization.uid;
    }

    updateParams(newParams);

    if (
      window.location.pathname.includes("next-actions") &&
      parsedNextActionsFilters?.leadTypeNames
    ) {
      updateParams({ savedNames: parsedNextActionsFilters?.leadTypeNames });
    } else if (parsedHomeFilters?.leadTypeNames) {
      updateParams({ savedNames: parsedHomeFilters?.leadTypeNames });
    }
  }, [
    initialParams?.organizationUIds,
    selectedOrganization,
    selectedOrganizations,
  ]);

  useEffect(() => {
    if (searchTerm.trim() !== "") {
      updateParams({ name: searchTerm });
    } else {
      setQueryParams((prev) => {
        const { name, ...rest } = prev;
        return rest;
      });
    }
  }, [searchTerm]);
  const {
    data: leadTypesFilterOptions,
    error: leadTypesFilterOptionsError,
    isLoading: leadTypesFilterOptionsLoading,
    refetch: refetchLeadTypesFilterOptions,
  } = useQuery({
    queryKey: ["getLeadTypesFilterOptions", queryParams, extraParams],
    queryFn: () => fetchLeadTypesLookups({ ...queryParams, ...extraParams }),
    ...queryCommonFlags,
    staleTime: 60000,
    enabled: !!queryParams.organizationUIds,
  });

  return {
    refetchLeadTypesFilterOptions,
    leadTypesFilterOptions: leadTypesFilterOptions?.data.results,
    leadTypesFilterOptionsError,
    leadTypesFilterOptionsLoading,
    handleSearchChange,
    setSearchTerm,
    searchTerm,
  };
};

export default useLeadTypesFilterOptions;
